/**
 * Display-only grouping for LinguistLanguage rows.
 *
 * Locked design (2026-10-06): storage is one row per
 * (LinguistID, LanguageID, Mode) with Mode ∈ {'All','Consecutive',
 * 'Simultaneous'} — a language may have TWO rows (Consecutive +
 * Simultaneous). Lists/profiles must not render that as a duplicated
 * language, so this helper folds the rows into one entry per language.
 *
 * Rate resolution mirrors server/helpers/linguistRates.js chains exactly:
 *   base         : Consecutive → All
 *   simultaneous : Simultaneous → All → Consecutive
 * Rows whose Rate is null / empty / zero / non-numeric are skipped (legacy
 * zero rows exist — zero is never a real rate). No cross-language fallback.
 *
 * Consumers format the resolved numbers per surface via
 * buildGroupedRateDisplayToken(group, formatRate?):
 *   - Linguists list valueGetter → verbose token, joined with " | " (raw
 *     numbers; feeds filter options / saved filters / sorting/quick search
 *     — keep stable, do NOT compact this one)
 *   - LinguistProfile       → "Name (token)", raw numbers
 *   - linguistTooltipHelper → "Name (token)", rates via toFixed(2)
 * The Linguists list CELL renders compactly via
 * buildCompactRateDisplayToken ("$X / $Y", legend in the cell tooltip —
 * Amine review, 2026-10-06).
 */

const LINGUIST_LANGUAGE_MODES = ["All", "Simultaneous", "Consecutive"];

const normalizeMode = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "All";
  return (
    LINGUIST_LANGUAGE_MODES.find(
      (mode) => mode.toLowerCase() === normalized.toLowerCase(),
    ) || "All"
  );
};

const toUsableRate = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric;
};

const getRowLanguageId = (row) =>
  row?.LanguageID ??
  row?.language_id ??
  row?.Language?.id ??
  row?.language?.id ??
  null;

/**
 * Group LinguistLanguage rows by language into one display entry per
 * language (first-seen row order).
 *
 * @param {Array<object>} rows - LinguistLanguages rows
 *   ({ Rate, Mode, LanguageID, Language?{id, Name} }; snake_case accepted).
 * @returns {Array<{
 *   languageId: number|string|null,
 *   name: string,
 *   baseRate: number|null,
 *   baseSourceMode: 'All'|'Consecutive'|'Simultaneous'|null,
 *   simultaneousRate: number|null,
 *   simultaneousSourceMode: 'All'|'Consecutive'|'Simultaneous'|null,
 *   hasDistinctSimultaneous: boolean,
 * }>} One entry per language. Rates stay null when no usable row exists so
 *   callers can still list the language without inventing a rate.
 */
export const groupLinguistLanguagesByLanguage = (rows) => {
  const groups = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== "object") continue;

    const languageId = getRowLanguageId(row);
    const key =
      languageId === null || languageId === undefined
        ? "__unknown-language__"
        : String(languageId);

    if (!groups.has(key)) {
      groups.set(key, {
        languageId,
        name: "",
        ratesByMode: new Map(),
      });
    }

    const group = groups.get(key);

    if (!group.name) {
      group.name = row?.Language?.Name || row?.Language?.name || "";
    }

    const rate = toUsableRate(row?.Rate ?? row?.rate);
    if (rate === null) continue; // legacy zero/null rows carry no rate

    const mode = normalizeMode(row?.Mode ?? row?.mode);
    if (!group.ratesByMode.has(mode)) {
      group.ratesByMode.set(mode, rate); // first usable row per mode wins
    }
  }

  return Array.from(groups.values()).map((group) => {
    const rateFor = (...modes) => {
      for (const mode of modes) {
        if (group.ratesByMode.has(mode)) return group.ratesByMode.get(mode);
      }
      return null;
    };
    const sourceModeFor = (...modes) => {
      for (const mode of modes) {
        if (group.ratesByMode.has(mode)) return mode;
      }
      return null;
    };

    const baseRate = rateFor("Consecutive", "All");
    const simultaneousRate = rateFor("Simultaneous", "All", "Consecutive");

    return {
      languageId: group.languageId,
      name: group.name,
      baseRate,
      baseSourceMode: sourceModeFor("Consecutive", "All"),
      simultaneousRate,
      simultaneousSourceMode: sourceModeFor(
        "Simultaneous",
        "All",
        "Consecutive",
      ),
      hasDistinctSimultaneous:
        baseRate !== null &&
        simultaneousRate !== null &&
        baseRate !== simultaneousRate,
    };
  });
};

/**
 * Build the display token for one grouped language (UI vocabulary locked in
 * Ysera's review, 2026-10-06):
 *   - split modes       → "Consecutive $X · Simultaneous $Y"
 *   - simultaneous only → "Simultaneous $Y"
 *   - equal pair / 'All' / single row → "$X"
 * Returns null when the group has no usable rate.
 *
 * @param {ReturnType<typeof groupLinguistLanguagesByLanguage>[number]} group
 * @param {(rate: number) => string} [formatRate] - per-surface number style
 *   (default `$${rate}`; the tooltip passes toFixed(2)).
 * @returns {string|null}
 */
export const buildGroupedRateDisplayToken = (
  group,
  formatRate = (rate) => `$${rate}`,
) => {
  if (!group) return null;

  if (group.hasDistinctSimultaneous) {
    return `Consecutive ${formatRate(group.baseRate)} · Simultaneous ${formatRate(
      group.simultaneousRate,
    )}`;
  }

  if (group.baseRate === null && group.simultaneousRate !== null) {
    return `Simultaneous ${formatRate(group.simultaneousRate)}`;
  }

  const rate = group.baseRate ?? group.simultaneousRate;
  return rate === null ? null : formatRate(rate);
};

/**
 * Compact token for the Linguists list CELL (Amine review, 2026-10-06):
 *   - split modes       → "$X / $Y" (consecutive first, simultaneous second)
 *   - simultaneous only → "$Y"
 *   - equal pair / 'All' / single row → "$X"
 * The mode legend moves into the cell tooltip, so this token hides
 * "Consecutive"/"Simultaneous" labels on purpose. Null semantics match
 * buildGroupedRateDisplayToken exactly, so a group contributes a segment
 * precisely when it contributes a verbose token today.
 *
 * @param {ReturnType<typeof groupLinguistLanguagesByLanguage>[number]} group
 * @param {(rate: number) => string} [formatRate]
 * @returns {string|null}
 */
export const buildCompactRateDisplayToken = (
  group,
  formatRate = (rate) => `$${rate}`,
) => {
  if (!group) return null;

  if (group.hasDistinctSimultaneous) {
    return `${formatRate(group.baseRate)} / ${formatRate(
      group.simultaneousRate,
    )}`;
  }

  if (group.baseRate === null && group.simultaneousRate !== null) {
    return formatRate(group.simultaneousRate);
  }

  const rate = group.baseRate ?? group.simultaneousRate;
  return rate === null ? null : formatRate(rate);
};
